/* eslint-disable react/no-unescaped-entities */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Loader2, HardHat, Paperclip, X as XIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, LineReveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

const ROLES = [
  "Operaio generico / Manovale",
  "Applicatore poliurea / poliuretano",
  "Muratore",
  "Cartongessista",
  "Piastrellista / Pavimentista",
  "Imbianchino / Finiture",
  "Restauratore / Addetto microaeroabrasione",
  "Installatore fotovoltaico",
  "Carpentiere / Ferraiolo",
  "Consulente commerciale",
  "Altro",
];

const EMPTY = { name: "", email: "", phone: "", role: "", experience: "", availability: "", message: "" };
const MAX_CV_MB = 10;

const PERKS = [
  { t: "Cantieri in tutta Italia", d: "Lavori stabili e continuativi con base in Puglia e trasferte gestite dall'azienda." },
  { t: "Formazione tecnica", d: "Ti formiamo sulle tecnologie più innovative: poliurea, poliuretano, rivestimenti e restauro." },
  { t: "Squadra e sicurezza", d: "Attrezzature professionali, DPI e una squadra affiatata che lavora a regola d'arte." },
];

export default function LavoraConNoi() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cv, setCv] = useState(null); // { url, filename, size, uploading }
  const cvInputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickCv = () => cvInputRef.current?.click();

  const removeCv = () => {
    setCv(null);
    if (cvInputRef.current) cvInputRef.current.value = "";
  };

  const uploadCv = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Formato non supportato — carica un PDF, DOC o DOCX");
      return;
    }
    if (file.size > MAX_CV_MB * 1024 * 1024) {
      toast.error(`File troppo grande — max ${MAX_CV_MB} MB`);
      return;
    }
    setCv({ filename: file.name, size: file.size, uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/careers/upload-cv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCv({ url: data.url, filename: data.filename, size: data.size, uploading: false });
      toast.success("CV caricato");
    } catch (err) {
      setCv(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento del CV");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.role) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (cv?.uploading) {
      toast.error("Attendi il completamento del caricamento del CV.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (cv?.url) {
        payload.cv_url = cv.url;
        payload.cv_filename = cv.filename;
      }
      await api.post("/careers", payload);
      setSent(true);
      toast.success("Candidatura inviata! Ti ricontatteremo presto.");
      setForm(EMPTY);
      setCv(null);
      if (cvInputRef.current) cvInputRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="lavora-con-noi-page" className="bg-background">
      <section className="pt-40 pb-16 md:pt-48 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-8 flex items-center gap-3">
            <span>/ Lavora con noi</span><span className="w-16 h-px bg-primary" />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16">
            <h1 className="font-head font-black text-[1.9rem] min-[380px]:text-[2.3rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tightest leading-[0.95] flex-1">
              <LineReveal lines={["Costruisci", "il tuo futuro", "con noi."]} />
            </h1>
            <img
              src="/images/ghs-logo-full.png?v=4"
              alt="GHS SpA — Greenhouse Solutions SPA"
              width="636"
              height="340"
              data-testid="careers-logo"
              className="w-52 sm:w-64 md:w-80 lg:w-96 h-auto shrink-0 self-start lg:self-end lg:mb-3"
              loading="eager"
            />
          </div>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              Cerchiamo operai e professionisti dell'edilizia che vogliano crescere in un'azienda
              tecnica e in continua espansione. Invia la tua candidatura spontanea: la valuteremo con attenzione.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="space-y-px bg-border border border-border">
                {PERKS.map((p) => (
                  <div key={p.t} className="bg-card p-7">
                    <HardHat className="w-7 h-7 text-primary mb-4" />
                    <h3 className="font-head font-bold text-lg tracking-tight mb-2">{p.t}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{p.d}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {sent ? (
              <div data-testid="career-success" className="border border-border bg-card p-12 flex flex-col items-start">
                <CheckCircle2 className="w-14 h-14 text-primary mb-6" />
                <h2 className="font-head font-black text-3xl md:text-4xl tracking-tightest mb-4">Candidatura ricevuta.</h2>
                <p className="text-muted-foreground leading-relaxed max-w-lg">
                  Grazie per il tuo interesse verso Greenhouse Solutions. La tua candidatura è stata
                  inviata alla nostra segreteria: ti contatteremo qualora il tuo profilo fosse in linea con le nostre esigenze.
                </p>
                <p data-testid="career-success-email-note" className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-lg border-l-2 border-primary pl-4">
                  Ti abbiamo inviato una <strong className="text-foreground">email di conferma</strong> con il riepilogo della candidatura.
                  Se non la vedi, controlla la cartella spam.
                </p>
                <button onClick={() => setSent(false)} data-testid="career-new" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 hover:gap-3 transition-all">
                  Invia un'altra candidatura <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="career-form" className="border border-border bg-card p-8 md:p-12 space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest">Nome e cognome *</Label>
                    <Input id="name" data-testid="career-name" value={form.name} onChange={set("name")} placeholder="Mario Rossi" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-widest">Telefono *</Label>
                    <Input id="phone" data-testid="career-phone" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">Email *</Label>
                    <Input id="email" type="email" data-testid="career-email" value={form.email} onChange={set("email")} placeholder="mario@email.it" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="availability" className="font-mono text-xs uppercase tracking-widest">Disponibilità</Label>
                    <Input id="availability" data-testid="career-availability" value={form.availability} onChange={set("availability")} placeholder="Immediata / dal ..." className="rounded-none h-12" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest">Mansione *</Label>
                    <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                      <SelectTrigger data-testid="career-role" className="rounded-none h-12">
                        <SelectValue placeholder="Seleziona una mansione" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience" className="font-mono text-xs uppercase tracking-widest">Anni di esperienza</Label>
                    <Input id="experience" data-testid="career-experience" value={form.experience} onChange={set("experience")} placeholder="Es. 5 anni" className="rounded-none h-12" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-mono text-xs uppercase tracking-widest">Presentazione</Label>
                  <Textarea id="message" data-testid="career-message" value={form.message} onChange={set("message")} rows={5} placeholder="Raccontaci le tue competenze, i cantieri a cui hai lavorato, i tuoi punti di forza..." className="rounded-none" />
                </div>

                {/* CV UPLOAD */}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-widest">Curriculum Vitae (opzionale)</Label>
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => uploadCv(e.target.files?.[0])}
                    className="hidden"
                    data-testid="career-cv-input"
                  />
                  {!cv && (
                    <button
                      type="button"
                      onClick={pickCv}
                      data-testid="career-cv-pick"
                      className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-border bg-background/50 text-muted-foreground font-mono text-xs uppercase tracking-widest px-6 py-6 hover:border-primary hover:text-foreground hover:bg-primary/5 transition-colors cursor-pointer"
                    >
                      <Paperclip className="w-4 h-4" />
                      Allega il tuo CV — PDF, DOC o DOCX · max {MAX_CV_MB} MB
                    </button>
                  )}
                  {cv && (
                    <div data-testid="career-cv-attached" className="border border-border bg-secondary/40 px-4 py-3 flex items-center gap-3">
                      {cv.uploading ? (
                        <Loader2 className="w-4 h-4 shrink-0 animate-spin text-primary" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 shrink-0 text-primary" />
                      )}
                      <span className="flex-1 min-w-0 font-mono text-xs truncate">
                        {cv.filename} <span className="text-muted-foreground">· {(cv.size/1024/1024).toFixed(2)} MB</span>
                        {cv.uploading && <span className="text-muted-foreground ml-1">— caricamento…</span>}
                      </span>
                      <button
                        type="button"
                        onClick={removeCv}
                        data-testid="career-cv-remove"
                        aria-label="Rimuovi CV"
                        className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                        disabled={cv.uploading}
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <button type="submit" data-testid="career-submit" disabled={loading || cv?.uploading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-10 py-5 hover:bg-foreground transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Invio...</> : <>Invia candidatura <ArrowUpRight className="w-5 h-5" /></>}
                </button>

                <p data-testid="career-privacy-note" className="mt-4 text-xs text-muted-foreground leading-relaxed">
                  Inviando la candidatura dichiari di aver letto l'{" "}
                  <Link to="/privacy" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">informativa privacy</Link>.
                  Ti invitiamo a non inserire nel CV dati particolari (salute, opinioni politiche o sindacali, convinzioni religiose).
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
